import { useEffect, useState } from 'react';

import useDebounce from 'hooks/useDebounce';
import { useCoordinatesCache } from 'hooks/useCoordinatesCache';
import { geocodeAddress } from 'utils/geocoding';
import assertNever from 'helpers/assertNever';
import { DealMapSource } from './getDealMapSource';

export type ResolvedDealLocation = {
    center: { lat: number; lng: number } | null;
    source: DealMapSource['type'];
    isResolving: boolean;
};

const DEBOUNCE_MS = 1000;
const INITIAL_STATE: ResolvedDealLocation = { center: null, source: 'default', isResolving: false };

export const useResolvedDealLocation = (intent: DealMapSource): ResolvedDealLocation => {
    const [state, setState] = useState<ResolvedDealLocation>(INITIAL_STATE);
    const { getCachedCoordinates, setCachedCoordinates } = useCoordinatesCache();

    const intentKey = JSON.stringify(intent);
    const debouncedIntentKey = useDebounce(intentKey, DEBOUNCE_MS);

    useEffect(() => {
        let cancelled = false;
        const debouncedIntent = JSON.parse(debouncedIntentKey) as DealMapSource;

        const resolve = async () => {
            switch (debouncedIntent.type) {
                case 'coordinates':
                case 'market':
                    if (!cancelled) {
                        setState({
                            center: { lat: debouncedIntent.lat, lng: debouncedIntent.lng },
                            source: debouncedIntent.type,
                            isResolving: false,
                        });
                    }
                    return;

                case 'address': {
                    setState(prev => ({ ...prev, isResolving: true }));
                    const cached = getCachedCoordinates(debouncedIntent.query);
                    const resolved = cached ?? await geocodeAddress(debouncedIntent.query);
                    if (cancelled) return;
                    if (!resolved?.lat || !resolved?.lng) {
                        setState(prev => ({ ...prev, isResolving: false }));
                        return;
                    }
                    if (!cached) setCachedCoordinates(debouncedIntent.query, resolved);
                    setState({ center: { lat: resolved.lat, lng: resolved.lng }, source: 'address', isResolving: false });
                    return;
                }

                case 'default':
                    if (!cancelled) {
                        setState({ center: null, source: 'default', isResolving: false });
                    }
                    return;

                default:
                    return assertNever(debouncedIntent);
            }
        };

        resolve();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedIntentKey]);

    return state;
};
